import React, { FC, useState, useEffect } from 'react';
import { debounce } from 'debounce';
// common components
import { StyledMap, MapControl } from '@ems/client-design-system';
import { NoisePopup } from 'src/components/NoisePopup';
// functions
import {
  useMapRef,
  useMapApis,
  useMapProps,
  useMapConfig,
  useDatesDataForMap,
} from 'src/@app/functions/map';
import {
  useDataForMap,
  useMapLayer,
  useMapTracksFilter,
} from 'src/@app/functions/infringementsOnMap';
import { useLanguageSelectors } from 'src/@app/reducers/languageReducer';
import { useAuthSelectors } from 'src/@auth/reducers/authReducer';
import {
  flyTo,
  useMapHoverOperations,
  useMapHover,
  useMapClick,
  useMapSelection,
  useGeometryRequiredByMap,
} from 'src/utils';
import { useStaticDbDisplay } from 'src/utils/playback';
import { OperationPopup } from 'src/components';
import { useDataSelectors } from '../reducers';
import { IMapProps } from 'src/@infringements/interfaces';
import { useInfringementRulesSelectors } from 'src/@app/reducers';

export const MapContainer: FC<IMapProps> = ({ noiseMonitors = [], noiseData = [] }) => {
  // map ref
  const [mapNode, mapRef] = useMapRef();
  // get map apis
  const mapApis: any = useMapApis(mapNode);
  // get map props from config
  const { viewportFromProps, ...mapProps } = useMapProps('2D');
  // viewport in state
  const [viewport, setViewport] = useState(viewportFromProps);
  // get mapbox config values required to add source and styles
  const mapBoxConfig = useMapConfig();

  // restrict map pan
  const onViewportChange = viewport => {
    if (
      Math.abs(viewport.latitude - viewportFromProps.latitude) < mapBoxConfig.limitLatitude &&
      Math.abs(viewport.longitude - viewportFromProps.longitude) < mapBoxConfig.limitLongitude
    ) {
      setViewport(viewport);
    }
  };

  // getting map style layers
  const layers = useMapLayer({ mapApis, mapBoxConfig, maptype: 'infringements' });
  // changing map tiles when dates change
  const { dateRangeMapping, datesArray } = useDatesDataForMap(mapApis, mapBoxConfig, layers);

  const dataSelectors = useDataSelectors();
  const {
    requiredData,
    infringementTypes,
    addedToSelection,
    removedFromSelection,
  } = dataSelectors.getRequiredDataForMap();

  const infRuleSelectors = useInfringementRulesSelectors();
  let extraGateIds: number[] = [];

  requiredData.map(infringement => {
    if (infringement.infringementType === 'Gate') {
      extraGateIds = extraGateIds.concat(infRuleSelectors.getGateIdsByRuleId(infringement.ruleId));
    }
  });

  useGeometryRequiredByMap(infringementTypes, mapApis, mapBoxConfig);

  // getting coordinates for infringements
  const { infringementTracks, selectedInfringements } = useDataForMap(
    'inf-browser',
    mapApis,
    mapBoxConfig,
    viewport,
    setViewport,
    dateRangeMapping,
    requiredData,
    addedToSelection,
    removedFromSelection,
    extraGateIds
  );
  // extrack map filters for the features
  const { infringementFilter, clickFilter } = useMapTracksFilter(
    infringementTracks.operations,
    selectedInfringements
  );

  // resets map view
  const resetView = () => {
    if (mapApis) {
      const resetViewport = Object.assign({}, viewportFromProps, { zoom: viewport.zoom });
      flyTo(mapApis, resetViewport).then(() => {
        setViewport(Object.assign({}, viewport, resetViewport));
      });
    }
  };

  // get field labels from language selectors
  const languageSelectors = useLanguageSelectors();
  const {
    fields: { operations: opsFields },
    abbreviations: { operations: opsAbbreviation },
  } = languageSelectors.getLanguage();
  const labels = Object.assign(opsFields, opsAbbreviation);

  // handle hover and get hovered operation
  const { hoveredOperation, handleHover, setHoveredOperation } = useMapHoverOperations(
    viewport,
    mapApis,
    datesArray,
    infringementFilter
  );

  // set map selection for the hover object
  useMapHover(hoveredOperation, mapApis);

  // Map selection
  const [showSelected, setShowSelected] = useState(false);
  const [selectedOperations, setSelectedOperations]: any = useState(null);

  // click handlers that provide selected operations
  const { handleClick, selectedOperation } = useMapClick(
    hoveredOperation,
    setShowSelected,
    mapApis,
    datesArray,
    clickFilter,
    selectedOperations,
    setSelectedOperations
  );

  // set feature state for the hovered operation
  useMapSelection(selectedOperations, mapApis, 'infringement-select', selectedInfringements);

  // reset hover and select when selection changes
  useEffect(() => {
    setShowSelected(false);
    setHoveredOperation(null);
    setSelectedOperations([]);
  }, [selectedInfringements.length]);

  const accessToken = useAuthSelectors().getAccessToken();

  const selectedMonitor: any[] = useStaticDbDisplay(mapApis, noiseMonitors, noiseData, false);

  const noisePopups: JSX.Element[] = [];
  if (selectedMonitor.length > 0) {
    selectedMonitor.map((monitor: any) => {
      noisePopups.push(
        <NoisePopup
          key={`popup_${monitor.properties.id}`}
          zoomLevel={mapApis.getZoom()}
          monitorData={monitor}
        />
      );
    });
  }

  return (
    <StyledMap
      viewport={viewport}
      onViewportChange={viewport => {
        viewport.maxPitch = 0;
        onViewportChange(viewport);
      }}
      {...mapProps}
      ref={mapRef}
      onClick={handleClick}
      onHover={debounce(handleHover, 5)}
      transformRequest={
        mapBoxConfig && mapBoxConfig.transformRequest && mapBoxConfig.transformRequest(accessToken)
      }>
      <MapControl resetView={resetView} />
      {noisePopups}
      {showSelected &&
        selectedOperation &&
        selectedOperation.latitude &&
        selectedOperation.longitude && (
          <OperationPopup
            selectedOperation={selectedOperation}
            onClose={() => setShowSelected(false)}
            labels={labels}
          />
        )}
      {!showSelected &&
        hoveredOperation &&
        hoveredOperation.latitude &&
        hoveredOperation.longitude && (
          <OperationPopup
            selectedOperation={hoveredOperation}
            onClose={() => {
              console.log(close);
            }}
            labels={labels}
          />
        )}
    </StyledMap>
  );
};
